import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import DataTable from "../../common/DataTable/DataTable";
import Toast from "../../common/Toast/Toast";
import EntityConstants from "../../configs/EntityConstants";
import CertificatePreviewModal from "../../pages/NotifyRecipients/CertificatePreviewModal";
import { DownloadIcon, EyeIcon } from "../icons";
import { GET } from "../../service/CRUDService";
import DownloadFile from "../../service/DownloadFile";
import { automationTestIds } from "../../testing/automationTestIds";
import { buildQueryString } from "../../utils/buildQueryString";
import { parseSuccessEnvelope } from "../../utils/parseSuccessEnvelope";
import ErrorHandling from "./ErrorHandling";

export const ISSUED_CERTIFICATES_PATH = "/issuer/discover/issued-certificates";

const TruncatedCell = ({ value, maxWidthClass = "max-w-[180px]" }) => {
  if (!value) {
    return null;
  }

  return (
    <span className={`inline-block ${maxWidthClass} truncate`} title={value}>
      {value}
    </span>
  );
};

const ActionIconButton = ({
  id,
  label,
  disabled,
  onClick,
  activeIcon,
  disabledIcon,
}) => (
  <button
    type="button"
    id={id}
    title={label}
    aria-label={label}
    disabled={disabled}
    onClick={disabled ? undefined : onClick}
    className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimaryButton focus-visible:ring-offset-1 ${
      disabled
        ? "cursor-not-allowed opacity-40 text-gray-400"
        : "cursor-pointer text-gray-700 hover:bg-[#E0EAFF] hover:text-cfPrimaryDark"
    }`}
  >
    {disabled ? disabledIcon : activeIcon}
  </button>
);

const RecentlyIssuedCertificates = ({
  pageLimit = 5,
  showPagination = false,
  showViewAll = true,
  showTitle = true,
  showSubtitle = true,
  compact = false,
  dataTestId = automationTestIds.dashboardRecentlyIssued,
  tableTestId = automationTestIds.dashboardRecentlyIssuedTable,
}) => {
  const { t } = useTranslation(["dashboard", "workspace", "history"]);
  const navigate = useNavigate();
  const isMountedRef = useRef(true);
  const requestIdRef = useRef(0);
  const fetchCertificatesRef = useRef(null);
  const resolvedPageLimit = Math.max(1, Number(pageLimit) || 5);

  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [previewCredential, setPreviewCredential] = useState(null);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchCertificates = useCallback(
    async (currentOffset) => {
      const requestId = ++requestIdRef.current;
      setLoading(true);
      setIsError(false);

      try {
        const response = await GET(
          `${EntityConstants.GET_ISSUANCE_REPORT}${buildQueryString({
            limit: resolvedPageLimit,
            offset: currentOffset,
          })}`,
        );
        const data = await parseSuccessEnvelope(response);

        if (!isMountedRef.current || requestId !== requestIdRef.current) {
          return;
        }

        const results = Array.isArray(data?.results) ? data.results : [];
        const totalCount = Number(data?.count) || 0;

        if (results.length === 0 && currentOffset > 0 && totalCount > 0) {
          const previousOffset = Math.max(0, currentOffset - resolvedPageLimit);
          if (previousOffset !== currentOffset) {
            fetchCertificatesRef.current?.(previousOffset);
            return;
          }
        }

        setRows(results);
        setCount(totalCount);
        setOffset(currentOffset);
      } catch (error) {
        console.error("Error fetching recently issued certificates:", error);
        if (!isMountedRef.current || requestId !== requestIdRef.current) {
          return;
        }
        setRows([]);
        setCount(0);
        setIsError(true);
      } finally {
        if (isMountedRef.current && requestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    },
    [resolvedPageLimit],
  );

  useEffect(() => {
    fetchCertificatesRef.current = fetchCertificates;
  }, [fetchCertificates]);

  useEffect(() => {
    fetchCertificates(0);
  }, [fetchCertificates]);

  const hasPrevious = offset > 0;
  const hasNext = offset + rows.length < count;

  const handlePrevious = useCallback(() => {
    if (!hasPrevious || loading) return;
    fetchCertificates(Math.max(0, offset - resolvedPageLimit));
  }, [fetchCertificates, hasPrevious, loading, offset, resolvedPageLimit]);

  const handleNext = useCallback(() => {
    if (!hasNext || loading) return;
    fetchCertificates(offset + resolvedPageLimit);
  }, [fetchCertificates, hasNext, loading, offset, resolvedPageLimit]);

  const handleOpenPreview = useCallback((credential) => {
    if (
      !credential?.credential_digital_preview &&
      !credential?.credential_print_preview
    ) {
      return;
    }
    setPreviewCredential(credential);
  }, []);

  const showDownloadMessage = useCallback(
    (isSuccess, status, message) => {
      Toast({
        type: isSuccess ? "success" : "error",
        title:
          status ||
          (isSuccess ? t("history:download") : t("history:download_error")),
        message:
          message ||
          (isSuccess
            ? t("history:download_certificate")
            : t("history:download_error_message")),
      });
    },
    [t],
  );

  const handleDownload = useCallback(
    (credentialId) => {
      if (!credentialId) return;
      DownloadFile(
        EntityConstants.DOWNLOAD_SVG_AS_PNG,
        credentialId,
        false,
        showDownloadMessage,
      );
    },
    [showDownloadMessage],
  );

  const columns = useMemo(
    () => [
      {
        key: "recipient_name",
        label: t("dashboard:holder_name"),
        render: (value) =>
          value ? (
            <TruncatedCell value={value} maxWidthClass="max-w-[160px]" />
          ) : (
            t("workspace:not_applicable")
          ),
      },
      {
        key: "programme_name",
        label: t("dashboard:programme"),
        render: (value) =>
          value ? (
            <TruncatedCell value={value} maxWidthClass="max-w-[200px]" />
          ) : (
            t("workspace:not_applicable")
          ),
      },
      {
        key: "batch_name",
        label: t("dashboard:batch"),
        render: (value) =>
          value ? (
            <TruncatedCell value={value} maxWidthClass="max-w-[180px]" />
          ) : (
            t("workspace:not_applicable")
          ),
      },
      {
        key: "credential_id",
        label: t("dashboard:credential_id"),
        render: (value) =>
          value ? (
            <span
              className="inline-flex items-center rounded-md bg-[#f4f0fc] px-2.5 py-1 text-xs font-semibold tracking-wide text-cfPrimaryDarkBlue"
              title={value}
            >
              {value}
            </span>
          ) : (
            t("workspace:not_applicable")
          ),
      },
      {
        key: "issued_on",
        label: t("dashboard:issued_on"),
        render: (value, row) =>
          value || row?.issued_at || t("workspace:not_applicable"),
      },
      {
        key: "actions",
        label: t("dashboard:actions"),
        sortable: false,
        render: (_value, row) => {
          const canPreview = Boolean(
            row?.credential_digital_preview || row?.credential_print_preview,
          );
          const canDownload = Boolean(row?.credential_id);

          return (
            <div className="inline-flex items-center justify-center gap-2">
              <ActionIconButton
                id={`${dataTestId}-preview-${row.credential_id}`}
                label={
                  canPreview
                    ? t("history:preview")
                    : t("workspace:certificate_preview_not_available")
                }
                disabled={!canPreview}
                onClick={() => handleOpenPreview(row)}
                activeIcon={
                  <EyeIcon size={20} className="inline-flex" aria-hidden="true" />
                }
                disabledIcon={
                  <EyeIcon
                    size={20}
                    className="inline-flex text-gray-400"
                    aria-hidden="true"
                  />
                }
              />
              <ActionIconButton
                id={`${dataTestId}-download-${row.credential_id}`}
                label={t("history:download")}
                disabled={!canDownload}
                onClick={() => handleDownload(row.credential_id)}
                activeIcon={
                  <DownloadIcon
                    size={20}
                    className="inline-flex"
                    aria-hidden="true"
                  />
                }
                disabledIcon={
                  <DownloadIcon
                    size={20}
                    className="inline-flex text-gray-400"
                    aria-hidden="true"
                  />
                }
              />
            </div>
          );
        },
      },
    ],
    [t, dataTestId, handleOpenPreview, handleDownload],
  );

  const showHeader =
    showTitle ||
    showSubtitle ||
    showViewAll ||
    (showPagination && !loading && !isError && count > 0);

  return (
    <div
      id={dataTestId}
      className={compact ? "bg-transparent" : "bg-white rounded-3xl p-4 sm:p-5"}
    >
      {showHeader && (
        <div
          className={`flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between ${
            compact ? "mb-3" : "mb-4"
          }`}
        >
          <div>
            {showTitle && (
              <h2 className="text-cfHeadingText font-semibold text-lg">
                {t("dashboard:recently_issued_certificates")}
              </h2>
            )}
            {showSubtitle && (
              <p className={`text-sm text-gray-500 ${showTitle ? "mt-1" : ""}`}>
                {t("dashboard:recently_issued_certificates_subtitle")}
              </p>
            )}
          </div>
          {showViewAll && (
            <button
              type="button"
              id={automationTestIds.dashboardRecentlyIssuedViewAll}
              onClick={() => navigate(ISSUED_CERTIFICATES_PATH)}
              className="inline-flex shrink-0 items-center gap-1.5 self-start px-1 py-1.5 text-sm font-semibold text-cfPrimaryButton transition-colors duration-150 hover:text-cfPrimaryDark focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimaryButton focus-visible:ring-offset-2 rounded-sm"
            >
              <span>{t("dashboard:view_all")}</span>
              <ArrowRight className="h-4 w-4 shrink-0" aria-hidden="true" />
            </button>
          )}
        </div>
      )}

      {isError && !loading ? (
        <ErrorHandling errorMessage="recently issued certificates" />
      ) : (
        <DataTable
          columns={columns}
          data={rows}
          loading={loading}
          loadingVariant="skeleton"
          skeletonRowCount={resolvedPageLimit}
          emptyMessage={t("dashboard:no_recent_certificates")}
          emptyHint={t("dashboard:no_recent_certificates_hint")}
          rowKey="credential_id"
          align="center"
          dataTestId={tableTestId}
          showPagination={showPagination}
          hasPrevious={hasPrevious}
          hasNext={hasNext}
          onPrevious={handlePrevious}
          onNext={handleNext}
        />
      )}

      {previewCredential && (
        <CertificatePreviewModal
          isOpen={Boolean(previewCredential)}
          onClose={() => setPreviewCredential(null)}
          previewUrl={previewCredential.credential_digital_preview}
          printPreviewUrl={previewCredential.credential_print_preview}
          credential_id={previewCredential.credential_id}
          isLoading={false}
        />
      )}
    </div>
  );
};

export default RecentlyIssuedCertificates;
